import React, { useState, useEffect, useRef } from "react";
import { Col, Dropdown, Menu, Modal, Row, Table, Tooltip } from "antd";
import {
  Close,
  Delete,
  Description,
  Download,
  MoreVert,
  Person,
  Search,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { documentValidation } from "../../../../../../utils";
import { useSelector } from "react-redux";
import { BeatLoader } from "react-spinners";
import { DocumentGridSkeleton, TableSkeleton } from "../../../../../../components/page-skeleton";
import { documentsManipulator } from "../../../../../../helpers/manuplator";
import { GridViewOutlined, List as ListIcon } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import "./styles.scss";

const DocumentsModal = ({ preview, previewHandler }) => {
  const [searchText, setsearchText] = useState("");
  const [uploadLoader, setuploadLoader] = useState(false);
  const [filesLoader, setFilesLoader] = useState(false);
  const [downloadLoader, setdownloadLoader] = useState([]);
  const [deletePreview, setDeletePreview] = useState(false);
  const [deleteLoader, setdeleteLoader] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [listView, setlistView] = useState(false);

  const { http } = global.services;
  const uploadImageInput = useRef(null);
  const selectedCompany = useSelector((state) => state.CompanyReducer.selected_company);

  const uploadFilesHelper = (newfiles) => {
    setuploadLoader(true);
    const data = new FormData();
    data.append("company_id", selectedCompany.id);
    data.append("timestamp", new Date().getTime());
    newfiles.forEach((file) => {
      data.append("documents", file);
    });
    http
      .post(`company-documents/${selectedCompany.id}`, data)
      .finally(() => setuploadLoader(false))
      .then((response) => {
        setFiles([...documentsManipulator(response.data), ...files]);
        toast.success(`Document${newfiles.length > 1 ? "s" : ""} uploaded successfully`);
      })
      .catch(() => {});
  };

  const selectUploadMediaHandler = (newfiles = []) => {
    const validFiles = [];
    for (let i = 0; i < newfiles.length; i++) {
      const file = newfiles[i];
      const isExist = files.find((item) => item.name === file.name);
      const isVaid = documentValidation(file);
      if (isExist) toast.error("File already exists");
      if (!isExist && isVaid) {
        validFiles.push(file);
      }
    }
    if (uploadImageInput.current) uploadImageInput.current.value = "";
    if (validFiles.length >= 1) uploadFilesHelper(validFiles);
  };

  const downloadFileHandler = (file) => {
    setdownloadLoader((prev) => [...prev, file.id]);

    fetch(file.downloadUrl, {
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob(); // Convert response to blob
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob); // Correctly create a blob URL
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.name); // Specify the file name
        document.body.appendChild(link);
        link.click(); // Trigger the download
        document.body.removeChild(link); // Clean up
        window.URL.revokeObjectURL(url); // Release the object URL
      })
      .catch(() => {})
      .finally(() => {
        setdownloadLoader((prev) => prev.filter((item) => item !== file.id)); // Remove loader for this file
      });
  };

  const deleteFilePreviewHandler = (file) => {
    setSelectedFile(file);
    setDeletePreview(true);
  };

  const deleteFileHandler = () => {
    setdeleteLoader(true);
    http
      .delete(`company-documents/${selectedFile.id}`)
      .finally(() => setdeleteLoader(false))
      .then(() => {
        toast.success("Document deleted successfully");
        setFiles((prev) => prev.filter((item) => item.id !== selectedFile.id));
        setDeletePreview(false);
      })
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    maxFiles: 100,
    onDropAccepted: (file) => {
      selectUploadMediaHandler(file);
    },
    onDropRejected: () => toast.error("Invalid file type"),
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
    },
    {
      title: "Uploaded On",
      dataIndex: "uploadedOn",
      key: "uploadedOn",
    },
    {
      title: "Actions",
      dataIndex: "",
      key: "actions",
      render: (data) => (
        <span className="actions">
          <button
            className="download"
            disabled={downloadLoader.includes(data.id)}
            onClick={() => downloadFileHandler(data)}
          >
            {downloadLoader.includes(data.id) ? <BeatLoader size={4} /> : <Download />}
          </button>
          <button className="delete" onClick={() => deleteFilePreviewHandler(data)}>
            <Delete />
          </button>
        </span>
      ),
    },
  ];
  const filteredFiles =
    searchText.length === 0
      ? files
      : files.filter((file) =>
          file.name.toLowerCase().includes(searchText.toLowerCase())
        );

  useEffect(() => {
    if (!preview) return;
    setFilesLoader(true);
    http
      .get(`company-documents/${selectedCompany.id}`)
      .finally(() => setFilesLoader(false))
      .then(({ data }) => setFiles(documentsManipulator(data)))
      .catch(({ data }) => toast.error(data?.error ?? "Something went wrong"));
  }, [preview]);

  return (
    <>
      <Modal
        visible={preview}
        onCancel={previewHandler}
        closable={false}
        footer={null}
        width={1300}
        className="documents-modal"
      >
        <div className="top-box">
          <button className="close-btn" onClick={previewHandler}>
            <Close />
          </button>
          <h2>Documents</h2>
          <p>
            Welcome to your company's document library. Here, you can access, upload, and
            manage important files and resources that are shared across your organization.
            This central hub ensures all members have the latest documents at their
            fingertips.
          </p>
        </div>
        <div className="upload-box">
          <input
            id="uploadDoc"
            type="file"
            multiple
            ref={uploadImageInput}
            onChange={({ target }) => selectUploadMediaHandler(target.files)}
          />
          <label htmlFor="uploadDoc">
            {uploadLoader ? <BeatLoader size={8} /> : "Upload File"}
          </label>
          <p>Supported types: pdf, doc(x), ppt(x), xls(x) and txt</p>
        </div>
        <div
          {...getRootProps({ className: "dropzone" })}
          className={`drop-file-box ${isDragActive ? "active" : ""}`}
        >
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="dropzone-overlay">
              <p> Drop the files here</p>
            </div>
          )}
          <div className="filter-box">
            <div className="search-box">
              <Search />
              <input
                type="text"
                placeholder="Name"
                value={searchText}
                onChange={({ target }) => setsearchText(target.value)}
              />
            </div>
            <div className="view-filters">
              <Tooltip title="Grid view">
                <button
                  className={!listView ? "selected" : ""}
                  onClick={() => setlistView(false)}
                >
                  <GridViewOutlined />
                </button>
              </Tooltip>
              <Tooltip title="List view">
                <button
                  className={listView ? "selected" : ""}
                  onClick={() => setlistView(true)}
                >
                  <ListIcon />
                </button>
              </Tooltip>
            </div>
          </div>
          {listView ? (
            <Table
              bordered
              columns={columns}
              className="documents-table"
              loading={{
                spinning: filesLoader,
                indicator: <TableSkeleton rows={8} />,
              }}
              dataSource={filesLoader ? [] : filteredFiles}
              pagination={false}
              scroll={{
                y: 500,
                x: "max-content",
              }}
            />
          ) : (
            <Row className="documents-grid" gutter={[16, 16]}>
              {filesLoader ? (
                <DocumentGridSkeleton count={8} />
              ) : (
                <>
                  {filteredFiles.length > 0 ? (
                    filteredFiles.map((file) => (
                      <Col xs={24} md={8} lg={6} xl={4}>
                        <div key={file.id} className="document-box">
                          <div className="preview-box">
                            {file.type === "PDF" ? (
                              <object
                                data={file.downloadUrl}
                                type="application/pdf"
                                width="100%"
                                height="100%"
                              >
                                <Description />
                              </object>
                            ) : (
                              <Description />
                            )}
                          </div>
                          <div className="detail">
                            <h4>{file.name}</h4>
                            <h5>
                              <Person /> {file.uploadedBy}
                            </h5>
                            <p>
                              <span>{file.uploadedOn}</span>
                              <span>{file.type}</span>
                            </p>
                            <Dropdown
                              getPopupContainer={(trigger) => trigger.parentNode}
                              overlay={
                                <Menu>
                                  <Menu.Item
                                    key="1"
                                    onClick={() => downloadFileHandler(file)}
                                  >
                                    Download
                                  </Menu.Item>
                                  <Menu.Item
                                    key="2"
                                    onClick={() => deleteFilePreviewHandler(file)}
                                  >
                                    Delete
                                  </Menu.Item>
                                </Menu>
                              }
                              trigger={["click"]}
                            >
                              <button className="more">
                                <MoreVert />
                              </button>
                            </Dropdown>
                          </div>
                        </div>
                      </Col>
                    ))
                  ) : (
                    <Col xs={24} className="no-data">
                      <p>No Data</p>
                    </Col>
                  )}
                </>
              )}
            </Row>
          )}
        </div>
      </Modal>
      <Modal
        visible={deletePreview}
        centered
        className="delete-doc-modal"
        footer={null}
        onCancel={() => setDeletePreview(false)}
        closable={false}
      >
        <button className="close-btn" onClick={() => setDeletePreview(false)}>
          <Close />
        </button>
        <h2>Delete Document</h2>
        <p>Are you sure you want to delete {selectedFile?.name}?</p>
        <div className="action-box">
          <button onClick={() => setDeletePreview(false)}>Cancel</button>
          <button className="delete" onClick={deleteFileHandler}>
            {deleteLoader ? <BeatLoader size={8} color="#fff" /> : "Delete"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default DocumentsModal;
